/**
 * The conditions of a gate (Feature 72, 4.2) or of a decision's if branch
 * (Feature 79 D22): all or any of them must hold, each a rule on one value
 * or a list test, and Add a condition. They read the node the gate or the
 * decision follows first, or anything upstream of it (D5); a list test's
 * item conditions read one item of its list (Feature 77 D21).
 */
import type { ReactNode } from 'react';

import type {
  LinkageJoin,
  LinkageListTest,
  LinkageRule,
  LinkageRules,
} from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Stack } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import type { Draft } from './draft.js';
import { itemReferences, specAt } from './items.js';
import { isListTest } from './lists.js';
import { ListTestEditor } from './ListTestEditor.js';
import { firstReference, forPlace, referencesFor, type Schemas } from './references.js';
import { RuleRow } from './RuleRow.js';

export function RulesEditor({
  draft,
  from,
  rules,
  schemas,
  id,
  dimmed = false,
  note,
  onChange,
}: {
  draft: Draft;
  /** The node the gate or the decision follows: its fields are read first. */
  from: string;
  rules: LinkageRules;
  schemas: Schemas;
  /** Unique on the page: a gate's, or one branch's. */
  id: string;
  dimmed?: boolean;
  /** Said under Run when. */
  note?: ReactNode;
  onChange: (rules: LinkageRules) => void;
}) {
  const references = forPlace(referencesFor(draft, { kind: 'link', from }, schemas), 'condition');
  const items = rules.items;
  const setItems = (next: (LinkageRule | LinkageListTest)[]) => onChange({ ...rules, items: next });
  const replaceItem = (i: number, next: LinkageRule | LinkageListTest) =>
    setItems(items.map((r, j) => (j === i ? next : r)));
  const itemsOf = (ref: string) =>
    itemReferences(
      specAt(draft, ref, schemas),
      references.find((r) => r.value === ref)?.name ?? ref,
    );
  return (
    <>
      <Stack gap={14} style={{ opacity: dimmed ? 0.55 : 1 }}>
        <Field id={`${id}-join`} label="Run when">
          <SegmentedControl
            items={[
              { value: 'all', label: 'All hold' },
              { value: 'any', label: 'Any holds' },
            ]}
            value={rules.join}
            onChange={(join) => onChange({ ...rules, join: join as LinkageJoin })}
          />
        </Field>
        {note}
        {items.map((rule, i) =>
          isListTest(rule) ? (
            <ListTestEditor
              key={i}
              index={i}
              test={rule}
              references={references}
              itemsOf={itemsOf}
              onChange={(next) => replaceItem(i, next)}
              onRemove={() => setItems(items.filter((_, j) => j !== i))}
            />
          ) : (
            <RuleRow
              key={i}
              index={i}
              rule={rule}
              references={references}
              onChange={(next) => replaceItem(i, next)}
              onList={(next) => replaceItem(i, next)}
              onRemove={() => setItems(items.filter((_, j) => j !== i))}
            />
          ),
        )}
      </Stack>
      <Button
        variant="ghost"
        size="sm"
        icon="add"
        onClick={() => setItems([...items, { ref: firstReference(references), op: 'exists' }])}
      >
        Add a condition
      </Button>
    </>
  );
}
